import type { ReactNode } from 'react';
import { createContext, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ui } from '../copy/ui';
import { Button } from './Button';
import { cx } from './cx';
import s from './Toast.module.css';

export type ToastTone = 'ok' | 'warn' | 'crit';

export interface ToastInput {
  message: string;
  /**
   * Rendered in mono, because it is read back character by character, by someone quoting
   * it to the operator.
   */
  requestId?: string;
  tone?: ToastTone;
}

/** Spelled out rather than extending ToastInput: a spread widens an optional property, and
 *  under exactOptionalPropertyTypes the widened one is a different type. */
interface ShownToast {
  id: number;
  message: string;
  requestId?: string | undefined;
  tone?: ToastTone | undefined;
}

export interface ToastApi {
  push: (toast: ToastInput) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE: Record<ToastTone, string | undefined> = { ok: s.ok, warn: s.warn, crit: s.crit };

/** Long enough to read a request id off the screen and write it down. */
const DISMISS_AFTER_MS = 8000;

/**
 * The clock stops while the pointer is over a toast or focus is inside it — someone is
 * reading it, or about to copy from it — and restarts at this once both have left: the
 * rest of a read, not a fresh eight seconds.
 */
const RESUME_AFTER_MS = 4000;

type Hold = 'pointer' | 'focus';

export function useToast(): ToastApi {
  const ctx = use(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ReadonlyArray<ShownToast>>([]);
  const timers = useRef(new Map<number, number>());
  /** What is holding each toast open: the pointer over it, focus inside it, or both. */
  const holds = useRef(new Map<number, Set<Hold>>());
  const nextId = useRef(1);

  const stopTimer = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      stopTimer(id);
      holds.current.delete(id);
      setToasts((list) => list.filter((toast) => toast.id !== id));
    },
    [stopTimer],
  );

  const startTimer = useCallback(
    (id: number, ms: number) => {
      stopTimer(id);
      timers.current.set(id, window.setTimeout(() => dismiss(id), ms));
    },
    [stopTimer, dismiss],
  );

  const hold = useCallback(
    (id: number, why: Hold) => {
      const held = holds.current.get(id) ?? new Set<Hold>();
      held.add(why);
      holds.current.set(id, held);
      stopTimer(id);
    },
    [stopTimer],
  );

  /** A release with nothing held is ignored, so the running clock is left alone. */
  const release = useCallback(
    (id: number, why: Hold) => {
      const held = holds.current.get(id);
      if (!held?.delete(why) || held.size > 0) return;
      holds.current.delete(id);
      startTimer(id, RESUME_AFTER_MS);
    },
    [startTimer],
  );

  const api = useMemo<ToastApi>(
    () => ({
      push: (toast) => {
        // A counter rather than a clock or a random draw: two toasts raised in the same
        // millisecond — one mutation retried, then failing again — would share a key.
        const id = nextId.current++;
        setToasts((list) => [...list, { ...toast, id }]);
        startTimer(id, DISMISS_AFTER_MS);
      },
    }),
    [startTimer],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) window.clearTimeout(timer);
      pending.clear();
    };
  }, []);

  return (
    <ToastContext value={api}>
      {children}
      {/* Mounted empty and never unmounted: a live region that arrives in the same paint as
          its first child is not reliably announced. The region is the only live region: a
          role="status" on each toast inside it is a second one, and some screen readers
          announce the toast twice. */}
      <div className={s.region} aria-live="polite">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cx(s.toast, toast.tone && TONE[toast.tone])}
            onPointerEnter={() => hold(toast.id, 'pointer')}
            onPointerLeave={() => release(toast.id, 'pointer')}
            onFocus={() => hold(toast.id, 'focus')}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) release(toast.id, 'focus');
            }}
          >
            <div className={s.text}>
              <p className={s.message}>{toast.message}</p>
              {toast.requestId !== undefined && (
                <p className={s.requestId} translate="no">
                  {toast.requestId}
                </p>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => dismiss(toast.id)}>
              {ui.dismiss}
            </Button>
          </div>
        ))}
      </div>
    </ToastContext>
  );
}

export default ToastProvider;
